// src/components/NotificationDropdown.jsx
// -----------------------------------------
// Social-style notification dropdown for logged-in user
// - Bell icon with unread badge
// - Dropdown with message, type label, "time ago"
// - Click item -> mark as read (+ go to topic if linked)
// - "Mark all as read" button
// -----------------------------------------

import React, { useContext, useState, useEffect, useRef } from "react";
import api from "../api/api";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { timeAgo } from "../utils/timeAgo";
import { lockAppScroll, unlockAppScroll } from "../utils/scrollLock";
import { buildLegacyTopicPath, buildTopicPath } from "../utils/publicPaths";
import "../styles/NotificationDropdown.css";

// Map raw notification type to a nicer label for UI
const getTypeLabel = (type) => {
  if (!type) return "Info";

  const map = {
    suggestion: "Suggestion",
    suggestion_accepted: "Suggestion Accepted",
    flag: "Flag",
    flag_threshold: "Heavy Flags",
    under_review: "Under Review",
    warning_required: "Warning (Required)",
    approved: "Approved",
    deleted: "Deleted",
    edit: "Edit",
    system: "System",
  };

  return map[type] || type;
};

function NotificationDropdown() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) lockAppScroll();
    else unlockAppScroll();

    return () => unlockAppScroll();
  }, [open]);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Wrapper element reference – used to detect clicks outside
  const dropdownRef = useRef(null);

  if (!user) return null;

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // Fetch notifications from backend
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/notifications`);
      const payload = res.data;
      setNotifications(
        Array.isArray(payload) ? payload : payload?.notifications || []
      );
    } catch (err) {
      console.error("Failed to load notifications:", err);
    } finally {
      setLoading(false);
    }
  };

    // English:
  // Do not auto-fetch notifications on every mount/reload.
  // This avoids unnecessary session traffic during page refresh.
  // We fetch when the dropdown is opened.
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setOpen(false);
    }
  }, [user]);

  // Toggle dropdown open/close
  const handleToggle = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);

    // Every time we open the dropdown, fetch latest notifications
    if (nextOpen && user) {
      await fetchNotifications();
    }
  };

  // Mark single notification as read
  const markAsRead = async (id) => {
    try {
      await api.put(`/api/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await api.put(`/api/notifications/read-all`, {});
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      await fetchNotifications();
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  };
   const getNotificationTopicPath = (notif) => {
    const topicWord =
      notif?.topic_word ||
      notif?.word ||
      notif?.topic?.word ||
      "";

    if (topicWord && String(topicWord).trim()) {
      return buildTopicPath({
        id: notif?.topic_id,
        word: topicWord,
      });
    }

    if (notif?.topic_id) {
      return buildLegacyTopicPath(notif.topic_id);
    }

    return null;
  };

  // When user clicks a notification:
  // - First mark it as read
  // - Then, if it is linked to a topic, navigate to that topic
  const handleNotificationClick = async (notif) => {
    // Don't allow acknowledging required warnings from dropdown.
    // WarningGate modal is the only place to acknowledge.
    const isRequiredWarning =
      notif?.type === "warning_required" || notif?.type === "warning";

    if (!isRequiredWarning) {
      await markAsRead(notif.id);
    }

  const topicPath = getNotificationTopicPath(notif);
        if (topicPath) {
      navigate(topicPath, {
        state:
          notif?.topic?.word || notif?.topic_word || notif?.word
            ? {
                topic: notif?.topic || {
                  id: notif?.topic_id,
                  word: notif?.topic_word || notif?.word || "",
                },
              }
            : undefined,
      });
    }
  };

  // Close dropdown when user clicks outside the bell/dropdown
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="notif-wrapper" ref={dropdownRef}>
      {/* Bell button */}
      <button type="button" className="notif-bell-btn" onClick={handleToggle}>
        <span role="img" aria-label="notifications">
          🔔
        </span>
        {unreadCount > 0 && (
          <span className="notif-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="notif-dropdown">
          {/* Header */}
            <div className="notif-header">
            <span>Notifications</span>

            <div className="notif-header-actions">
              {unreadCount > 0 && (
                <button className="notif-mark-all" onClick={markAllAsRead}>
                  Mark all as read
                </button>
              )}

              {/* Mobile close (CSS will show this only on mobile) */}
              <button
                type="button"
                className="notif-close-btn"
                onClick={() => setOpen(false)}
                aria-label="Close notifications"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {/* List */}
          <div className="notif-list">
            {loading ? (
              <div className="notif-empty">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notif-empty">No notifications yet</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notif-item ${!n.is_read ? "unread" : ""}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  {/* Icon Section */}
                  <div className="notif-icon">
                    {n.type === "flag" && (
                      <span className="icon-flag">🚩</span>
                    )}
                    {n.type === "edit" && (
                      <span className="icon-edit">✏️</span>
                    )}
                    {n.type === "suggestion" && (
                      <span className="icon-suggest">💬</span>
                    )}
                    {n.type === "suggestion_accepted" && (
                      <span className="icon-accept">✅</span>
                    )}
                    {n.type === "under_review" && (
                      <span className="icon-review">⚠️</span>
                    )}

                    {n.type === "warning_required" && (
                      <span className="icon-review">⚠️</span>
                    )}

                    {n.type === "approved" && (
                      <span className="icon-approved">✔️</span>
                    )}
                    {n.type === "deleted" && (
                      <span className="icon-deleted">🗑️</span>
                    )}
                  </div>

                  {/* Text Section */}
                  <div className="notif-content">
                    <div className="notif-message">{n.message}</div>

                    <div className="notif-meta">
                      <span className="notif-type">{getTypeLabel(n.type)}</span>
                      <span className="notif-time">
                        {n.created_at_epoch ? timeAgo(n.created_at_epoch) : ""}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
